import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { ReactNode } from "react";

import { CURRENCY_OFFSET } from "@/constants";
import { Address } from "@/types/api/shared";
import { I18nextLanguage, I18nextLocale } from "@/types/types";

// eslint-disable-next-line  @typescript-eslint/no-require-imports
require("dayjs/locale/nb");

// eslint-disable-next-line  @typescript-eslint/no-require-imports
const { getDayJSLocale } = require("@/utils/functions.js");

dayjs.extend(isToday);

// PHONE NUMBER
// ------------
export const formatPhone = (phone: string, prettify?: boolean): string => {
  const NORWEGIAN_COUNTRY_CODE = "47";
  const phoneNumberRaw =
    phone?.startsWith(NORWEGIAN_COUNTRY_CODE) && phone.length === 10
      ? phone
      : `${NORWEGIAN_COUNTRY_CODE}${phone}`;
  // If prettify is true, we will return the number split into groups of 4
  // with a "+" in front of the first group.
  if (prettify) {
    const phoneNumberSplit = phoneNumberRaw.replace(
      /^\s*([0-9]{2})\s*\-?([0-9]{3})\s*\-?([0-9]{2})\s*\-?([0-9]{3})$/,
      "($1) $2 $3 $4"
    );
    return `+${phoneNumberSplit}`;
  }
  return phoneNumberRaw;
};

export const capitaliseFirstLetter = (str: string): string => {
  if (str == null) return str;
  const strLower = str.toLowerCase();
  return strLower.charAt(0).toUpperCase() + strLower.slice(1);
};

// ADDRESS
// ------------
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatAddressObject = (address: Record<string, any>) => {
  const { street, streetNumber, floor, household } = address;

  const formattedAddress = {
    DIAddress: { ...address },
    formattedAddress: {
      street: street.streetName,
      streetnumber: streetNumber.streetNo + streetNumber.entrance,
      postalCode: streetNumber.postalCode,
      city: street.city,
      addressId: streetNumber.addressId,
      floor_number: floor?.floorNo,
      floor_type: floor?.floorType,
      flat_number: household?.flatNo,
    },
  };
  return formattedAddress;
};

export const formatAddressMarkup = (
  name: string,
  address: Address,
  hideName?: boolean
): ReactNode => {
  const {
    city,
    number,
    postcode,
    street,
    flat_number,
    floor_number,
    floor_type,
  } = address || {};
  return (
    <>
      {!hideName && (
        <>
          {name}
          <br />
        </>
      )}
      {flat_number && floor_number ? (
        <>
          {`Floor ${floor_type}${floor_number}, Flat ${flat_number}`}
          <br />
        </>
      ) : null}
      {capitaliseFirstLetter(street)} {number}
      <br />
      {postcode} {capitaliseFirstLetter(city)}
    </>
  );
};

// DATE
// ------------
export const formatDate = ({
  date,
  locale,
  format = "dddd D MMMM",
  todayLabel,
}: {
  date: number | string;
  locale: I18nextLanguage;
  format?: string;
  todayLabel?: string;
}): string => {
  const dayJsDate = dayjs(date);
  if (todayLabel && dayJsDate.isToday()) return todayLabel;
  const dateString = dayjs(date).locale(getDayJSLocale(locale)).format(format);
  return dateString;
};

// CONTAINER SIZE
// ------------
export const formatContainerSize = (
  size: number,
  unit: "L" | "ml" = "ml"
): string => {
  const divider = unit === "L" ? 1000 : 1;
  return `${size / divider} ${unit}`;
};

// FORMAT CURRENCY
// ------------
export const formatCurrency = (price: number, currency = "NOK"): string => {
  const offset = CURRENCY_OFFSET;
  const priceFormatted = new Intl.NumberFormat(I18nextLocale.NO_NO, {
    style: "currency",
    currency,
  })
    .format(price / offset)
    // trim as server has space at the start of this string and client doesn't :shrug
    // causes SSR mismatch
    .trim();

  return `${priceFormatted}`;
};

/*
 * Format a numerical value into a price, without a trailing currency string/indicator.
 *
 * The `locale` property is initialised to `en-IN` since the initial use case is for sending price
 * values to tracking pixels, like Meta and Snapchat. Those pixels require the prices to be internationalised
 * using an English locale (with a full stop as the fractional separator rather than a comma).
 *
 * */
export const formatPriceWithoutCurrencyString = (price: number): string => {
  const offset = CURRENCY_OFFSET;
  const priceFormatted = new Intl.NumberFormat("en-IN", {
    currency: "NOK",
    style: "currency",
    minimumFractionDigits: 2,
  })
    .format(price / offset)
    .substring(3)
    // trim as server has space at the start of this string and client doesn't :shrug
    // causes SSR mismatch
    .trim();

  return priceFormatted;
};

export const formatPricePerLitre = (
  price, // Price in smallest currency unit, e.g., cents
  size // Size in millilitres
) => {
  // Assuming the offset converts the smallest currency unit to a standard format
  const offset = CURRENCY_OFFSET;
  const sizeInLitres = size / 1000; // Convert size to litres
  const pricePerLitre = price / offset / sizeInLitres;
  const formattedPricePerLitre = pricePerLitre.toFixed(2); // Three decimal places for precision

  return `${formattedPricePerLitre} kr/L`; // Return price in "kr" per litre
};
